<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { stockDailyApi, tradeCalendarApi } from '@/api/stock'
import { useTableSort } from '@/composables/useTableSort'
import StockFilterPanel from '@/components/stock/StockFilterPanel.vue'
import StockDataTable from '@/components/stock/StockDataTable.vue'
import BasePagination from '@/components/common/BasePagination.vue'
import PageHero from '@/components/common/PageHero.vue'

const router = useRouter()
const route = useRoute()

const loading = ref(false)
const stockList = ref([])
const total = ref(0)
const pageNum = ref(1)
const pageSize = ref(10)
const latestTradeDate = ref('')

const filterForm = ref({
  tradeDate: '',
  symbol: '',
  minMarketCap: 100,
  lowerBoundaryFirstBull: true,
  lowerBoundaryVolumeConfirm: true
})
const pctChgRange = ref({
  min: null,
  max: null
})
const pctChgBucketKey = ref(null)
const pctChgBucketLabel = ref('')

const pctChgRangeLabel = computed(() => {
  if (pctChgBucketLabel.value) return pctChgBucketLabel.value
  if (pctChgRange.value.min === null || pctChgRange.value.max === null) return ''
  return `${pctChgRange.value.min}%~${pctChgRange.value.max}%`
})

const { sortField, sortOrder, handleSort } = useTableSort({
  defaultField: 'symbol',
  defaultOrder: 'asc',
  getDefaultOrder: field => field === 'amount' || field === 'turnover' ? 'desc' : 'asc',
  onChange: () => loadData()
})

function mapStockItem (item) {
  return {
    id: item.id,
    symbol: item.symbol,
    name: item.stock_name || item.name || '-',
    tradeDate: item.trade_date,
    open: item.open,
    high: item.high,
    low: item.low,
    close: item.close,
    volume: item.volume,
    amount: item.amount,
    amplitude: item.amplitude,
    pctChg: item.pct_chg,
    priceChange: item.price_change,
    turnover: item.turnover,
    ma5: item.ma5,
    ma10: item.ma10,
    ma20: item.ma20,
    ma30: item.ma30,
    ma60: item.ma60,
    volMa5: item.vol_ma5,
    volMa10: item.vol_ma10,
    turnoverMa5: item.turnover_ma5,
    turnoverMa10: item.turnover_ma10,
    chg5d: item.chg_5d,
    chg10d: item.chg_10d,
    netProfitYoy: item.net_profit_yoy,
    roe: item.roe,
    score: item.score
  }
}

function toNumberOrNull (value) {
  if (value === undefined || value === null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function applyRouteQuery () {
  const query = route.query || {}
  if (query.trade_date) {
    filterForm.value.tradeDate = String(query.trade_date)
  }
  if (query.symbol) {
    filterForm.value.symbol = String(query.symbol)
  }
  if (query.lower_boundary_first_bull === '0' || query.lower_boundary_first_bull === 'false') {
    filterForm.value.lowerBoundaryFirstBull = false
  } else if (query.lower_boundary_first_bull === '1' || query.lower_boundary_first_bull === 'true') {
    filterForm.value.lowerBoundaryFirstBull = true
  }
  if (query.lower_boundary_volume_confirm === '0' || query.lower_boundary_volume_confirm === 'false') {
    filterForm.value.lowerBoundaryVolumeConfirm = false
  } else if (query.lower_boundary_volume_confirm === '1' || query.lower_boundary_volume_confirm === 'true') {
    filterForm.value.lowerBoundaryVolumeConfirm = true
  }
  const minMarketCap = toNumberOrNull(query.float_market_cap_min ?? query.min_market_cap)
  if (minMarketCap !== null) {
    filterForm.value.minMarketCap = minMarketCap
  }
  pctChgRange.value = {
    min: toNumberOrNull(query.pct_chg_min),
    max: toNumberOrNull(query.pct_chg_max)
  }
  pctChgBucketKey.value = toNumberOrNull(query.pct_chg_bucket_key)
  pctChgBucketLabel.value = query.pct_chg_label ? String(query.pct_chg_label) : ''
}

async function loadLatestTradeDate () {
  try {
    const date = await tradeCalendarApi.getLatest()
    if (date) {
      if (!route.query?.trade_date) {
        filterForm.value.tradeDate = date
      }
      latestTradeDate.value = date
      return date
    }
  } catch (error) {
    console.error('获取最近交易日失败:', error)
  }
  filterForm.value.tradeDate = ''
  latestTradeDate.value = ''
  return ''
}

async function loadData () {
  loading.value = true
  try {
    const params = {
      page_num: pageNum.value,
      page_size: pageSize.value,
      sort_field: sortField.value,
      sort_order: sortOrder.value
    }
    if (filterForm.value.tradeDate) {
      params.trade_date = filterForm.value.tradeDate
    }
    const trimmedSymbol = filterForm.value.symbol?.trim()
    if (trimmedSymbol) {
      params.symbol = trimmedSymbol
    }
    if (filterForm.value.minMarketCap != null && filterForm.value.minMarketCap !== '') {
      params.float_market_cap_min = filterForm.value.minMarketCap
    }
    if (filterForm.value.lowerBoundaryFirstBull) {
      params.lower_boundary_first_bull = true
      if (filterForm.value.lowerBoundaryVolumeConfirm) {
        params.lower_boundary_volume_confirm = true
      }
    }
    if (pctChgBucketKey.value !== null) {
      params.pct_chg_bucket_key = pctChgBucketKey.value
    }
    if (pctChgRange.value.min !== null) {
      params.pct_chg_min = pctChgRange.value.min
    }
    if (pctChgRange.value.max !== null) {
      params.pct_chg_max = pctChgRange.value.max
    }

    const res = await stockDailyApi.getShortTermBasePool(params)
    if (res?.trade_date) {
      filterForm.value.tradeDate = res.trade_date
    }
    stockList.value = (res?.list || []).map(mapStockItem)
    total.value = res?.total || 0
  } catch (error) {
    console.error('加载短线基础池失败:', error)
    stockList.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

async function handleSearch () {
  pctChgRange.value = { min: null, max: null }
  pctChgBucketKey.value = null
  pctChgBucketLabel.value = ''
  pageNum.value = 1
  await loadData()
}

async function handleReset () {
  filterForm.value.tradeDate = latestTradeDate.value || ''
  filterForm.value.symbol = ''
  filterForm.value.minMarketCap = 100
  filterForm.value.lowerBoundaryFirstBull = true
  filterForm.value.lowerBoundaryVolumeConfirm = true
  pctChgRange.value = { min: null, max: null }
  pctChgBucketKey.value = null
  pctChgBucketLabel.value = ''
  sortField.value = 'symbol'
  sortOrder.value = 'asc'
  pageNum.value = 1
  await loadData()
}

async function handlePageChange (newPage) {
  pageNum.value = newPage
  await loadData()
}

async function handlePageSizeChange (newSize) {
  pageSize.value = newSize
  pageNum.value = 1
  await loadData()
}

function handleRowDblClick (item) {
  if (!item.symbol) return
  router.push({
    name: 'trend-channel',
    params: { symbol: item.symbol },
    query: { name: item.name || '' }
  })
}

onMounted(async () => {
  await loadLatestTradeDate()
  applyRouteQuery()
  await loadData()
})
</script>

<template>
  <div class="short-term-base page">
    <PageHero>
      <template #actions>
        <div v-if="pctChgRangeLabel" class="range-chip">涨幅 {{ pctChgRangeLabel }}</div>
        <div v-if="filterForm.lowerBoundaryFirstBull" class="range-chip">下轨首阳</div>
        <div v-if="filterForm.lowerBoundaryFirstBull && filterForm.lowerBoundaryVolumeConfirm" class="range-chip">量能确认</div>
      </template>
    </PageHero>

    <StockFilterPanel
      v-model="filterForm"
      :show-market-cap="true"
      :show-turnover="false"
      :show-amount="false"
      :show-board-filters="false"
      :show-lower-boundary-first-bull="true"
      @search="handleSearch"
      @reset="handleReset"
    />

    <StockDataTable
      :list="stockList"
      :sort-field="sortField"
      :sort-order="sortOrder"
      :loading="loading"
      @sort="handleSort"
      @row-dblclick="handleRowDblClick"
    />

    <BasePagination
      v-if="total > 0"
      :page-num="pageNum"
      :page-size="pageSize"
      :total="total"
      @update:page-num="handlePageChange"
      @update:page-size="handlePageSizeChange"
    />
  </div>
</template>

<style scoped lang="scss">
.short-term-base {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px 28px 18px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100%;
    overflow: visible;
    padding: 4px 16px 12px;
  }
}

.range-chip {
  padding: 6px 12px;
  border: 1px solid var(--rule);
  border-radius: 4px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 11px;
}
</style>
